<template>
  <div class="flex-1 flex flex-col overflow-hidden bg-white p-3">

    <div class="grid grid-cols-6 gap-2 py-2">
      <input
        v-model="search"
        @change="fetch"
        type="text"
        placeholder="Search by Name or BRN"
        class="col-span-2 w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
      <select v-model="status" @change="fetch"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option :value="null">Filter by Status</option>
        <option value="Registered">Registered</option>
        <option value="Insolvent">Insolvent</option>
        <option value="Ceased">Ceased</option>
      </select>
      <div class="col-span-3 flex justify-end gap-2">
        <button @click="openForm" class="btn-sm-primary">
          <i class="fa fa-plus"></i> Strike/Blacklist Company
        </button>
        <button @click="openNameChangeForm" class="btn-sm-primary">
          <i class="fa fa-plus"></i> Name Change Strike Off
        </button>
      </div>
    </div>

    <div class="bg-white shadow overflow-hidden flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 border-b border-gray-100 sticky top-0">
          <tr>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">#</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">BRN</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Added By</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
          </tr>
          </thead>
          <tbody>
          <template v-if="!loading && entities.length > 0">
            <tr v-for="(entity, idx) in entities" :key="idx"
                class="border-b border-gray-100 hover:bg-gray-50 transition">
              <td class="py-3 px-4 font-bold">{{ (currentPage - 1) * limit + idx + 1 }}</td>
              <td class="py-3 px-4 font-bold text-primary cursor-pointer hover:underline" @click="viewFile(entity.brn)">
                {{ entity.brn }}
              </td>
              <td class="py-3 px-4 font-bold cursor-pointer hover:underline" @click="viewFile(entity.brn)">
                {{ entity.companyName }}
              </td>
              <td class="py-3 px-4">{{ dateFormat(entity.createdAt) }}</td>
              <td class="py-3 px-4">{{ entity.addedBy }}</td>
              <td class="py-3 px-4 flex gap-2">
                <button @click="getReversalHistory(entity.brn)" class="btn-sm-primary" title="View History">
                  <i class="fa fa-eye"></i> History
                </button>
                <button @click="reverseBlackList(entity.brn)" class="btn-sm-danger" title="Reverse">
                  Reverse
                </button>
              </td>
            </tr>
          </template>
          <tr v-if="loading">
            <td colspan="6" class="py-16 text-center">
              <i class="fa fa-spinner fa-spin text-primary text-2xl"></i>
            </td>
          </tr>
          <template v-if="!loading && entities.length === 0">
            <tr>
              <td colspan="6" class="py-16 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <i class="fa fa-folder-open text-6xl mb-4"></i>
                  <p class="text-lg font-medium text-gray-500">No Blacklisted Entities Found</p>
                  <p class="text-sm text-gray-400 mt-2">No blacklisted companies in the registry.</p>
                </div>
              </td>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
      <Pagination :pagination="pagination" :page="currentPage" :limit="limit" label="entities" @update="update" />
    </div>
  </div>

  <AppModal v-model="showViewReversalHistory" size="lg" title="View Blacklist Reversal History">
    <div class="p-4">
      <table class="w-full text-sm mb-4">
        <tbody>
        <tr class="border-b border-gray-100">
          <td class="py-2 font-semibold text-gray-600 w-1/3">Name</td>
          <td class="py-2">{{ reversalHistory.companyName }}</td>
        </tr>
        <tr class="border-b border-gray-100">
          <td class="py-2 font-semibold text-gray-600">Date Added</td>
          <td class="py-2">{{ reversalHistory.createdAt }}</td>
        </tr>
        <tr class="border-b border-gray-100">
          <td class="py-2 font-semibold text-gray-600">Date Updated</td>
          <td class="py-2">{{ reversalHistory.updatedAt }}</td>
        </tr>
        <tr>
          <td class="py-2 font-semibold text-gray-600">Added By</td>
          <td class="py-2">{{ reversalHistory.addedBy }}</td>
        </tr>
        </tbody>
      </table>
      <p class="font-semibold text-gray-700 mb-2">Historical Changes</p>
      <table class="w-full text-xs">
        <thead class="bg-gray-50">
        <tr>
          <th class="text-left py-2 px-3 font-semibold text-gray-600">Date</th>
          <th class="text-left py-2 px-3 font-semibold text-gray-600">Action</th>
          <th class="text-left py-2 px-3 font-semibold text-gray-600">Done By</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(rev, idx) in reversalHistory.history" :key="idx" class="border-b border-gray-100">
          <td class="py-2 px-3">{{ rev.createdAt }}</td>
          <td class="py-2 px-3">
              <span v-if="rev.fromStatus === 'OFF'" class="text-green-600">
                <i class="fa fa-arrow-left mr-1"></i>Removed from blacklist
              </span>
            <span v-else class="text-red-600">
                <i class="fa fa-arrow-right mr-1"></i>Added to blacklist
              </span>
          </td>
          <td class="py-2 px-3">{{ rev.addedBy }}</td>
        </tr>
        </tbody>
      </table>
    </div>
  </AppModal>

  <AppModal v-model="showStrikeCompanyModal" size="lg" title="Add Companies to De-register">
    <div class="p-4">
      <div class="border-b border-gray-200 mb-4">
        <nav class="flex space-x-6 px-4" aria-label="Tabs">
          <button
            class="whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer"
            :class="option === 'form' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            @click="select('form')">
            Enter BRN
          </button>
          <button
            class="whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer"
            :class="option === 'upload' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            @click="select('upload')">
            Upload File
          </button>
        </nav>
      </div>
      <form v-if="option === 'form'" class="max-w-md mx-auto">
        <input
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mb-3"
          v-model="form.brn"
          placeholder="Add BRN here" />
        <div class="flex justify-end">
          <button class="btn-sm-primary" @click="save" type="button" :disabled="spinner">
            <i v-if="spinner" class="fa fa-spinner fa-spin mr-1"></i> Submit
          </button>
        </div>
      </form>
      <form @submit.prevent="saveUpload" v-if="option === 'upload'" class="max-w-md mx-auto">
        <input
          type="file"
          @change="handleFileChange"
          accept=".csv, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          class="block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary mb-3" />
        <div class="flex justify-end">
          <button class="btn-sm-primary" type="submit" :disabled="spinner">
            <i v-if="spinner" class="fa fa-spinner fa-spin mr-1"></i> Upload
          </button>
        </div>
      </form>
    </div>
  </AppModal>

  <AppModal v-model="showStrikeChangeNameModal" size="lg" title="Strike Off the Register">
    <div class="p-4">
      <form @submit.prevent="saveNameChangeUpload" class="max-w-md mx-auto">
        <input
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mb-3"
          v-model="form.brn"
          placeholder="Add BRN here" />
        <input
          type="file"
          @change="handleFileChange"
          accept="application/pdf"
          class="block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary mb-3" />
        <div class="flex justify-end">
          <button class="btn-sm-primary" type="submit" :disabled="spinner">
            <i v-if="spinner" class="fa fa-spinner fa-spin mr-1"></i> Upload
          </button>
        </div>
      </form>
    </div>
  </AppModal>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import AppModal from "@/components/ui/AppModal.vue";
import { dateFormat } from "@/commons/composables/utilities.ts";
import { api } from "@/app/services/api.service.ts";
import { useToast } from "@/composables";
import { useBusinessRegistryStore } from "@/stores/business-registry.ts";
import Pagination from "@/commons/components/Pagination.vue";
import type { Pagination as PaginationType } from "@/app/types";

interface Entity {
  brn: string;
  companyName: string;
  createdAt: string;
  addedBy: string;
}

interface Form {
  file: File | null;
  brn: string | null;
}

interface HistoryItem {
  createdAt: string;
  fromStatus: string;
  addedBy: string;
}

interface ReversalHistory {
  brn: string | null;
  companyName: string;
  createdAt: string;
  updatedAt: string;
  addedBy: string;
  lastStatusUpdateDoneBy: string;
  history: HistoryItem[];
}

const registryStore = useBusinessRegistryStore();
const router = useRouter();
const { successToast, errorToast } = useToast();

const showViewReversalHistory = ref(false);
const showStrikeCompanyModal = ref(false);
const showStrikeChangeNameModal = ref(false);
const loading = ref(false);
const currentPage = ref(1);
const limit = ref(15);
const entities = ref<Entity[]>([]);
const pagination = ref<PaginationType | undefined>(undefined);
const search = ref("");
const status = ref<string | null>(null);
const reversalHistory = ref<ReversalHistory>({
  brn: null,
  companyName: "",
  createdAt: "",
  updatedAt: "",
  addedBy: "",
  lastStatusUpdateDoneBy: "",
  history: []
});
const form = ref<Form>({ file: null, brn: null });
const option = ref("form");
const spinner = ref(false);

onMounted(() => {
  fetch();
});

const fetch = () => {
  loading.value = true;
  registryStore.fetchBlackListRegistry({
    search: search.value,
    page: currentPage.value,
    limit: limit.value,
    offset: limit.value,
    status: "ON"
  });
};

const select = (route: string) => {
  option.value = route;
};

const handleFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) form.value.file = file;
};

const saveUpload = () => {
  const formData = new FormData();
  if (form.value.file) formData.set("file", form.value.file);
  spinner.value = true;
  registryStore.uploadBatchBrnsForStrikingOff(formData);
};

const saveNameChangeUpload = () => {
  const formData = new FormData();
  if (form.value.file) formData.set("file", form.value.file);
  if (form.value.brn) formData.set("brn", form.value.brn);
  formData.set("type", "CHANGE_OF_NAME");
  spinner.value = true;
  registryStore.uploadBatchBrnsForNameChangeStrikingOff(formData);
};

const viewFile = (brn: string) => {
  localStorage.setItem("entityType", "building-society");
  router.push("/home/non-individuals/file-viewer/" + brn);
  router.go(0);
};

const reverseBlackList = (brn: string) => {
  loading.value = true;
  api()
    .post("/data-update/blacklisted-brns/" + brn, brn)
    .then(() => {
      successToast("Success", "Removed from the blacklist");
      fetch();
    })
    .catch(error => {
      loading.value = false;
      errorToast("Error", error.response?.data?.message || "Failed to remove from blacklist");
    });
};

const getReversalHistory = async (brn: string) => {
  await api()
    .get("/data-update/blacklisted-brns/" + brn)
    .then(response => {
      reversalHistory.value = response.data.data;
      showViewReversalHistory.value = true;
    })
    .catch(error => {
      errorToast("Error", error.response?.data?.message || "Failed to load reversal history");
    });
};

const openForm = () => {
  showStrikeCompanyModal.value = true;
};

const openNameChangeForm = () => {
  showStrikeChangeNameModal.value = true;
};

const save = async () => {
  spinner.value = true;
  await api().post("/integration/get-entity-full", { registration_no: form.value.brn, old_registration_no: "-" })
    .then(response => {
      api()
        .post("/data-update/blacklisted-brns/" + form.value.brn + "/add", {
          brn: form.value.brn,
          companyName: response.data.entity_name
        })
        .then(() => {
          successToast("Success", "Added to blacklist");
          spinner.value = false;
          form.value.brn = null;
        })
        .catch(error => {
          errorToast("Error", error.response?.data?.message || "Failed to add to blacklist");
          spinner.value = false;
        });
    })
    .catch(error => {
      errorToast("Error", error.response?.data?.message || "Failed to process request");
      spinner.value = false;
    });
};

function update(pg: number) {
  currentPage.value = pg;
  fetch();
}

watch(() => registryStore.black_list_registry, (data) => {
  if (data) {
    loading.value = false;
    entities.value = data.content || [];
    pagination.value = {
      currentPage: currentPage.value,
      nextPage: currentPage.value + 1,
      totalRows: data.totalElements || 0,
      totalPages: data.totalPages || 0
    };
  }
});

watch(() => registryStore.entity_file, () => {
  spinner.value = false;
});

watch(() => registryStore.error, (data) => {
  if (data) {
    loading.value = false;
    errorToast("Error", data.message);
    spinner.value = false;
  }
});
</script>
